import { sumMeasuredAmounts } from './issueMasp';

export interface ParetoSliceInput {
  id: string;
  label: string;
  occurrenceCount: number | null;
  amount: number | null;
  measureId: string | null;
}

export interface ParetoBar {
  id: string;
  label: string;
  value: number;
  percent: number;
  cumulative: number;
}

export interface ParetoSeries {
  basis: 'amount' | 'count';
  measureId: string | null;
  bars: ParetoBar[];
}

function finite(value: number | null): value is number {
  return value != null && Number.isFinite(value);
}

function barsFrom(rows: { id: string; label: string; value: number }[]): ParetoBar[] | null {
  if (rows.length < 2) return null;
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  if (!(total > 0)) return null;
  const ordered = [...rows].sort((left, right) => (
    right.value - left.value || left.label.localeCompare(right.label, 'pt-BR')
  ));
  let running = 0;
  return ordered.map((row) => {
    running += row.value;
    return {
      id: row.id,
      label: row.label,
      value: row.value,
      percent: (row.value / total) * 100,
      cumulative: (running / total) * 100,
    };
  });
}

/**
 * Pareto só quando todas as fatias compartilham a mesma grandeza.
 * Valor exige a mesma medida. Contagem só entra se nenhuma fatia tiver valor.
 */
export function paretoSeries(
  slices: readonly ParetoSliceInput[],
  unitByMeasure: (measureId: string) => string | null,
): ParetoSeries | null {
  if (slices.length < 2) return null;
  const amounts = slices.filter((slice) => finite(slice.amount));
  if (amounts.length > 0) {
    if (amounts.length !== slices.length) return null;
    const measureIds = new Set(amounts.map((slice) => slice.measureId).filter((id): id is string => Boolean(id)));
    if (measureIds.size !== 1) return null;
    const measureId = [...measureIds][0];
    const unit = unitByMeasure(measureId);
    if (!unit) return null;
    try {
      sumMeasuredAmounts(amounts.map((slice) => ({
        measureId,
        unit,
        amount: slice.amount as number,
      })));
    } catch {
      return null;
    }
    const bars = barsFrom(amounts.map((slice) => ({
      id: slice.id,
      label: slice.label,
      value: slice.amount as number,
    })));
    return bars ? { basis: 'amount', measureId, bars } : null;
  }

  if (!slices.every((slice) => finite(slice.occurrenceCount))) return null;
  const bars = barsFrom(slices.map((slice) => ({
    id: slice.id,
    label: slice.label,
    value: slice.occurrenceCount as number,
  })));
  return bars ? { basis: 'count', measureId: null, bars } : null;
}
